/**
 * Active notes-editor registry + pending-write queue.
 *
 * Many code paths outside the React tree need to write notes JSON into a
 * deck's Y.Doc — card-notes-cleanup, AI-generation recovery, PDF
 * transcription seed, one-shot "get-ai-deck" hydrate, etc. They all
 * share the same problem: writing JSON into a `Y.XmlFragment` requires
 * a Tiptap ProseMirror schema, and that schema only exists inside the
 * live `NotesView` editor.
 *
 * This module is the glue:
 *   1. `NotesView` registers the mounted editor per NOTE on ready,
 *      unregisters on unmount.
 *   2. Non-React writers call one of two things:
 *      - `writeNotesContent(deckId, json, { noteId, onlyIfEmpty })` — a
 *        whole-document REPLACE. Only for content that fills a note (a
 *        seed, a recovery, a legacy import), and those pass `onlyIfEmpty`.
 *      - `updateNotesContent(deckId, transform, { noteId })` — a TRANSFORM
 *        of whatever the note contains AT THE MOMENT OF WRITING (a mark
 *        removed, a mark re-added). The transform is handed the live
 *        editor's JSON and returns the next document, or `null` for
 *        "nothing to change".
 *      If an editor is registered the write is applied at once through
 *      `editor.commands.setContent(json)`, which pushes through Tiptap's
 *      Collaboration extension into the Y.Doc. If none is, the write is
 *      queued and the next registration drains the queue in order.
 *
 * ── Why a transform, and never a precomputed document ─────────────────
 * A queued write lands LATER — after the note's real content has synced in,
 * and possibly after another device has edited it. A writer that reads the
 * note, computes a full document and queues THAT is queueing a snapshot: by
 * the time it drains, the snapshot is stale, and `setContent` would replace
 * the paragraph the phone added in the meantime with the old text — on every
 * device. That is exactly how a card deleted from the flashcards view used to
 * erase later edits to the note it was made in. A transform carries only the
 * INTENT ("remove these marks") and is evaluated against the live document
 * when it is applied, so it can never put back text that has since changed.
 *
 * Caveats:
 *   - `setContent` replaces the entire fragment. Concurrent edits during
 *     the write window are lost on this tab — matches the prior LWW
 *     behaviour of the old `sync.notes.setForDeck`. A future refactor
 *     could move to surgical Y.XmlText format() calls to preserve
 *     concurrent text edits while only touching targeted nodes.
 *   - The queue lives in module memory. A writer that fires for a note
 *     whose editor never mounts leaves the entry dangling until the
 *     session ends. That's acceptable — the alternative is IDB-backing
 *     every background write, and in practice the notes editor mounts
 *     whenever notes are relevant (AI gen triggers it, share flow
 *     already reads Y.Doc). Flows that must land WITHOUT an editor (a
 *     merge, an import, a copy) write the note's Y.Doc directly instead —
 *     see `yjs/note-doc-copy`.
 *   - Anything that should only fill an EMPTY note (a transcript seed,
 *     AI-generation recovery, the legacy import) must pass
 *     `{ onlyIfEmpty: true }`: emptiness is then judged on the live editor
 *     at the moment of writing, never at queue time. Deciding at queue
 *     time is how a fresh browser — whose note had not loaded yet, so it
 *     "looked" empty — replaced a user's edited notes with the original
 *     transcript on every device.
 *   - Both guards fail CLOSED on an editor whose document cannot be read:
 *     an `onlyIfEmpty` replace is dropped, and a transform stays queued
 *     for the next registration rather than being applied to a guess.
 */

import { isNoteDocEffectivelyEmpty } from '../notes/note-emptiness';
import { DEFAULT_NOTE_ID } from '../notes/note-identity';
import type { Editor } from './internal/editor-types';
import type { JSONContent } from './internal/editor-types';

/**
 * Keyed by NOTE, not by deck: a deck holds several, and two open notes on one
 * deck registering under the same key would each overwrite the other — a
 * cleanup write meant for Note 3 landing in whichever note registered last.
 * `\x01` is the byte a deck path cannot contain (see `mindmapRecordKey`).
 */
const noteKey = (deckId: string, noteId: string) => `${deckId}\x01${noteId}`;

export interface WriteNotesContentOptions {
  /**
   * WHICH note of the deck to write. Defaults to the deck's default note —
   * the one every deck has had since before a deck could hold several, so a
   * writer that predates that (the transcript seed, the legacy import) still
   * reaches the note it always did.
   */
  noteId?: string;
  /**
   * Only write if the note is effectively empty AT WRITE TIME, judged on the
   * live editor's document (the shared `isNoteDocEffectivelyEmpty`). If the
   * write is queued, the check runs when it is applied; a note that has
   * content by then is left untouched and the write is dropped.
   */
  onlyIfEmpty?: boolean;
}

export interface UpdateNotesContentOptions {
  /** WHICH note of the deck to transform — see `WriteNotesContentOptions`. */
  noteId?: string;
}

export type WriteNotesContentResult =
  /** Applied to the mounted editor. */
  | 'written'
  /** No editor mounted yet — applied (or dropped) when one registers. */
  | 'queued'
  /** `onlyIfEmpty` and the live note has content — nothing was written. */
  | 'skipped-not-empty';

export type UpdateNotesContentResult =
  /** Applied to the mounted editor. */
  | 'written'
  /** No editor mounted (or its content is unreadable) — applied when one
   *  registers with a readable document. */
  | 'queued'
  /** The transform found nothing to change on the live document. */
  | 'skipped-no-change';

/**
 * A transform over the live document. Returns the next document, or `null`
 * to leave the note untouched (nothing is written, nothing is queued).
 * Must not mutate its input.
 */
export type NotesContentTransform = (current: JSONContent) => JSONContent | null;

type PendingOp =
  | {
      kind: 'replace';
      json: JSONContent;
      onlyIfEmpty: boolean;
      result?: WriteNotesContentResult;
    }
  | {
      kind: 'transform';
      transform: NotesContentTransform;
      result?: UpdateNotesContentResult;
    };

const activeEditors = new Map<string, Editor>();
/** Per note, in the order the writers fired. */
const pendingOps = new Map<string, PendingOp[]>();

/**
 * Register a mounted notes editor for one note of a deck. Returns the
 * unregister fn.
 *
 * If there are writes queued for this note (a cleanup / AI-gen write that
 * fired before any editor was mounted), the registration drains them
 * immediately, in order — each replace subject to its `onlyIfEmpty` guard
 * and each transform evaluated against this editor's document as it is
 * at that moment.
 *
 * Register only an editor whose note has finished its server sync: a write
 * judged against a not-yet-synced editor would see the local (possibly
 * empty, possibly stale) state rather than the note's real content.
 */
export function registerActiveNotesEditor(
  deckId: string,
  editor: Editor,
  noteId: string = DEFAULT_NOTE_ID,
): () => void {
  const key = noteKey(deckId, noteId);
  activeEditors.set(key, editor);
  drainPendingContent(deckId, noteId);
  return () => {
    if (activeEditors.get(key) === editor) activeEditors.delete(key);
  };
}

/** Look up the mounted editor for a note, if any. */
export function getActiveNotesEditor(
  deckId: string,
  noteId: string = DEFAULT_NOTE_ID,
): Editor | undefined {
  return activeEditors.get(noteKey(deckId, noteId));
}

/** The live document, or `undefined` when it cannot be read. */
function readEditorJSON(editor: Editor): JSONContent | undefined {
  try {
    const json = editor.getJSON();
    return json && typeof json === 'object' ? (json as JSONContent) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Is the live editor's note safe to fill? Fails closed: a doc that can't be
 * read counts as NOT empty, so a guarded write never lands on it.
 */
function editorNoteIsEffectivelyEmpty(editor: Editor): boolean {
  const json = readEditorJSON(editor);
  if (!json) return false;
  try {
    return isNoteDocEffectivelyEmpty(json);
  } catch {
    return false;
  }
}

/**
 * Apply one op to the editor. Returns `false` when the op could NOT be
 * decided (a transform over an unreadable document) and must stay queued.
 */
function applyOp(editor: Editor, op: PendingOp): boolean {
  if (op.kind === 'replace') {
    if (op.onlyIfEmpty && !editorNoteIsEffectivelyEmpty(editor)) {
      op.result = 'skipped-not-empty';
      return true;
    }
    editor.commands.setContent(op.json);
    op.result = 'written';
    return true;
  }

  const current = readEditorJSON(editor);
  if (!current) return false;
  const next = op.transform(current);
  if (next === null || next === current) {
    op.result = 'skipped-no-change';
    return true;
  }
  editor.commands.setContent(next);
  op.result = 'written';
  return true;
}

/**
 * Queue an op and try to deliver it (and everything queued before it) to the
 * mounted editor. Ops are always delivered in the order they were queued, so
 * a write that fires while an earlier transform is still waiting on an
 * unreadable document lines up behind it rather than jumping ahead.
 */
function enqueue(key: string, op: PendingOp): void {
  const queue = pendingOps.get(key);
  if (queue) queue.push(op);
  else pendingOps.set(key, [op]);
  drainKey(key);
}

function drainKey(key: string): void {
  const editor = activeEditors.get(key);
  const queue = pendingOps.get(key);
  if (!editor || editor.isDestroyed || !queue || queue.length === 0) return;
  while (queue.length > 0) {
    const op = queue[0];
    if (!applyOp(editor, op)) return; // stays queued, with everything behind it
    queue.shift();
  }
  pendingOps.delete(key);
}

/**
 * REPLACE one note of a deck with `json`. Goes through the mounted editor
 * when available (Collaboration extension translates to proper Yjs
 * updates); otherwise queues until an editor for THAT note mounts.
 *
 * Semantics: replaces the full fragment content. This is only right for
 * content that FILLS a note — a seed, a recovery, an import — and every such
 * caller passes `{ onlyIfEmpty: true }` (see the module docblock). A caller
 * that wants to change part of an existing note must use
 * `updateNotesContent`: a document computed now and written later is a stale
 * snapshot by the time it lands.
 */
export function writeNotesContent(
  deckId: string,
  json: JSONContent,
  options: WriteNotesContentOptions = {},
): WriteNotesContentResult {
  const key = noteKey(deckId, options.noteId ?? DEFAULT_NOTE_ID);
  const op: PendingOp = {
    kind: 'replace',
    json,
    onlyIfEmpty: options.onlyIfEmpty === true,
  };
  enqueue(key, op);
  return op.result ?? 'queued';
}

/**
 * TRANSFORM one note of a deck. `transform` receives the note's document as it
 * is at the moment of writing — the live editor's, now or when one registers —
 * and returns the next document, or `null` for "nothing to change" (then
 * nothing is written). Several queued transforms compose in order, each seeing
 * the previous one's result. A document that cannot be read keeps the
 * transform queued (fail closed) rather than applying it to a guess.
 */
export function updateNotesContent(
  deckId: string,
  transform: NotesContentTransform,
  options: UpdateNotesContentOptions = {},
): UpdateNotesContentResult {
  const key = noteKey(deckId, options.noteId ?? DEFAULT_NOTE_ID);
  const op: PendingOp = { kind: 'transform', transform };
  enqueue(key, op);
  return op.result ?? 'queued';
}

/**
 * If writes are queued for the note, deliver them to the mounted editor in
 * order. Called on register and can also be invoked defensively.
 * A guarded (`onlyIfEmpty`) replace whose note now has content is dropped,
 * not kept for a later editor; a transform over an unreadable document is
 * kept, with everything queued behind it.
 */
export function drainPendingContent(
  deckId: string,
  noteId: string = DEFAULT_NOTE_ID,
): void {
  drainKey(noteKey(deckId, noteId));
}
